import React, { useMemo } from "react";
import styled from "styled-components";
import bibleData from "../../data/bible.json";

// 성경 권 순서
const bookOrder = [
  "창",
  "출",
  "레",
  "민",
  "신",
  "수",
  "삿",
  "룻",
  "삼상",
  "삼하",
  "왕상",
  "왕하",
  "대상",
  "대하",
  "스",
  "느",
  "에",
  "욥",
  "시",
  "잠",
  "전",
  "아",
  "사",
  "렘",
  "애",
  "겔",
  "단",
  "호",
  "욜",
  "암",
  "옵",
  "욘",
  "미",
  "나",
  "합",
  "습",
  "학",
  "슥",
  "말",
  "마",
  "막",
  "눅",
  "요",
  "행",
  "롬",
  "고전",
  "고후",
  "갈",
  "엡",
  "빌",
  "골",
  "살전",
  "살후",
  "딤전",
  "딤후",
  "딛",
  "몬",
  "히",
  "약",
  "벧전",
  "벧후",
  "요일",
  "요이",
  "요삼",
  "유",
  "계",
];

// 성경 권 전체 이름 -> 약어 매핑
const bookAbbreviations: { [key: string]: string } = {
  창세기: "창",
  출애굽기: "출",
  레위기: "레",
  민수기: "민",
  신명기: "신",
  여호수아: "수",
  사사기: "삿",
  룻기: "룻",
  사무엘상: "삼상",
  사무엘하: "삼하",
  열왕기상: "왕상",
  열왕기하: "왕하",
  역대상: "대상",
  역대하: "대하",
  에스라: "스",
  느헤미야: "느",
  에스더: "에",
  욥기: "욥",
  시편: "시",
  잠언: "잠",
  전도서: "전",
  아가: "아",
  이사야: "사",
  예레미야: "렘",
  예레미야애가: "애",
  에스겔: "겔",
  다니엘: "단",
  호세아: "호",
  요엘: "욜",
  아모스: "암",
  오바댜: "옵",
  요나: "욘",
  미가: "미",
  나훔: "나",
  하박국: "합",
  스바냐: "습",
  학개: "학",
  스가랴: "슥",
  말라기: "말",
  마태복음: "마",
  마가복음: "막",
  누가복음: "눅",
  요한복음: "요",
  사도행전: "행",
  로마서: "롬",
  고린도전서: "고전",
  고린도후서: "고후",
  갈라디아서: "갈",
  에베소서: "엡",
  빌립보서: "빌",
  골로새서: "골",
  데살로니가전서: "살전",
  데살로니가후서: "살후",
  디모데전서: "딤전",
  디모데후서: "딤후",
  디도서: "딛",
  빌레몬서: "몬",
  히브리서: "히",
  야고보서: "약",
  베드로전서: "벧전",
  베드로후서: "벧후",
  요한일서: "요일",
  요한이서: "요이",
  요한삼서: "요삼",
  유다서: "유",
  요한계시록: "계",
};

interface ScriptureTextProps {
  scriptures: string[];
}

interface VerseInfo {
  book: string;
  chapter: string;
  verse: string;
  text: string;
}

interface ScripturePosition {
  book: string;
  chapter: number;
  verse: number;
}

const bible = bibleData as {
  [book: string]: { [chapter: string]: { [verse: string]: string } };
};

// ========== 헬퍼 함수들 ==========

/** 권 이름을 약어로 변환 */
const toBookAbbr = (bookName: string): string => {
  return bookAbbreviations[bookName.trim()] || bookName.trim();
};

/** 숫자 키를 정렬된 배열로 반환 */
const getSortedNumericKeys = (obj: object): number[] => {
  return Object.keys(obj)
    .map(Number)
    .sort((a, b) => a - b);
};

/** 특정 구절 텍스트 가져오기 */
const getVerseText = (
  book: string,
  chapter: number,
  verse: number,
): string | undefined => {
  return bible[book]?.[String(chapter)]?.[String(verse)];
};

/** VerseInfo 객체 생성 */
const createVerseInfo = (
  book: string,
  chapter: number,
  verse: number,
): VerseInfo | null => {
  const text = getVerseText(book, chapter, verse);
  return text
    ? { book, chapter: String(chapter), verse: String(verse), text }
    : null;
};

/** 위치가 시작 위치 이전인지 확인 */
const isBeforeStart = (
  current: { book: number; chapter: number; verse: number },
  start: ScripturePosition,
  startBookIndex: number,
): boolean => {
  if (current.book < startBookIndex) return true;
  if (current.book === startBookIndex) {
    if (current.chapter < start.chapter) return true;
    if (current.chapter === start.chapter && current.verse < start.verse)
      return true;
  }
  return false;
};

/** 위치가 끝 위치 이후인지 확인 */
const isAfterEnd = (
  current: { book: number; chapter: number; verse: number },
  end: ScripturePosition,
  endBookIndex: number,
): boolean => {
  if (current.book > endBookIndex) return true;
  if (current.book === endBookIndex) {
    if (current.chapter > end.chapter) return true;
    if (current.chapter === end.chapter && current.verse > end.verse)
      return true;
  }
  return false;
};

// ========== 파싱 함수들 ==========

/** 시작 부분 파싱: "창세기 1:1" -> { book, chapter, verse } */
const parseStartPart = (part: string): ScripturePosition | null => {
  const match = part.match(/^(.+?)\s*(\d+):(\d+)$/);
  if (!match) return null;

  const [, bookName, chapter, verse] = match;
  return {
    book: toBookAbbr(bookName),
    chapter: parseInt(chapter),
    verse: parseInt(verse),
  };
};

/** 끝 부분 파싱: "10", "2:5", "출애굽기 2:5" 형식 지원 */
const parseEndPart = (
  part: string,
  start: ScripturePosition,
): ScripturePosition => {
  // "10" - 같은 장의 절만
  if (/^\d+$/.test(part)) {
    return { ...start, verse: parseInt(part) };
  }

  // "2:5" - 같은 권의 다른 장
  const chapterVerseMatch = part.match(/^(\d+):(\d+)$/);
  // "출애굽기 2:5" - 다른 권
  const fullMatch = part.match(/^(.+?)\s*(\d+):(\d+)$/);

  if (chapterVerseMatch) {
    return {
      book: start.book,
      chapter: parseInt(chapterVerseMatch[1]),
      verse: parseInt(chapterVerseMatch[2]),
    };
  }

  if (fullMatch) {
    return {
      book: toBookAbbr(fullMatch[1]),
      chapter: parseInt(fullMatch[2]),
      verse: parseInt(fullMatch[3]),
    };
  }

  return start;
};

// ========== 구절 수집 함수들 ==========

/** 범위 내 모든 구절 수집 */
const collectVersesInRange = (
  start: ScripturePosition,
  end: ScripturePosition,
): VerseInfo[] => {
  const verses: VerseInfo[] = [];

  const startBookIndex = bookOrder.indexOf(start.book);
  const endBookIndex = bookOrder.indexOf(end.book);

  if (startBookIndex === -1 || endBookIndex === -1) return verses;

  for (let bookIdx = startBookIndex; bookIdx <= endBookIndex; bookIdx++) {
    const currentBook = bookOrder[bookIdx];
    const bookData = bible[currentBook];
    if (!bookData) continue;

    const chapters = getSortedNumericKeys(bookData);

    for (const chapter of chapters) {
      const chapterData = bookData[String(chapter)];
      if (!chapterData) continue;

      const verseNums = getSortedNumericKeys(chapterData);

      for (const verse of verseNums) {
        const current = { book: bookIdx, chapter, verse };

        // 범위 체크
        if (isBeforeStart(current, start, startBookIndex)) continue;
        if (isAfterEnd(current, end, endBookIndex)) continue;

        const verseInfo = createVerseInfo(currentBook, chapter, verse);
        if (verseInfo) verses.push(verseInfo);
      }
    }
  }

  return verses;
};

// ========== 메인 파싱 함수 ==========

/** 성경 구절 문자열을 파싱하여 VerseInfo 배열 반환 */
const parseScripture = (scripture: string): VerseInfo[] => {
  if (!scripture) return [];

  try {
    const parts = scripture.split(/\s*-\s*/);

    // 단일 구절: "창세기 1:1"
    if (parts.length === 1) {
      const position = parseStartPart(scripture);
      if (!position) return [];

      const verseInfo = createVerseInfo(
        position.book,
        position.chapter,
        position.verse,
      );
      return verseInfo ? [verseInfo] : [];
    }

    // 범위: "창세기 1:1-10" 또는 "창세기 1:1 - 2:5" 등
    if (parts.length === 2) {
      const start = parseStartPart(parts[0].trim());
      if (!start) return [];

      const end = parseEndPart(parts[1].trim(), start);
      return collectVersesInRange(start, end);
    }

    return [];
  } catch (error) {
    console.error("Error parsing scripture:", error);
    return [];
  }
};

// 약어 -> 전체 이름 역방향 매핑
const bookFullNames: { [key: string]: string } = Object.entries(
  bookAbbreviations,
).reduce(
  (acc, [fullName, abbr]) => {
    acc[abbr] = fullName;
    return acc;
  },
  {} as { [key: string]: string },
);

const ScriptureText: React.FC<ScriptureTextProps> = ({ scriptures }) => {
  // scriptures 배열 필터링
  const scriptureParts = useMemo(() => {
    return scriptures.filter((s) => s.trim());
  }, [scriptures]);

  const allVerses = useMemo(() => {
    return scriptureParts.map((part) => ({
      reference: part.trim(),
      verses: parseScripture(part.trim()),
    }));
  }, [scriptureParts]);

  // 모든 본문이 비어있으면 표시하지 않음
  if (allVerses.every((part) => part.verses.length === 0)) {
    return null;
  }

  // 장이 변경되는 지점 추적
  const getChapterLabel = (
    verse: VerseInfo,
    prevVerse: VerseInfo | null,
  ): string | null => {
    // 첫 번째 절이거나, 권/장이 바뀌면 표시
    if (
      !prevVerse ||
      verse.book !== prevVerse.book ||
      verse.chapter !== prevVerse.chapter
    ) {
      const bookName = bookFullNames[verse.book] || verse.book;
      return `${bookName} ${verse.chapter}장`;
    }
    return null;
  };

  // 타이틀에 표시할 본문 목록
  const scriptureTitle = scriptureParts.join(" / ");

  return (
    <Container>
      <Header>
        <Title>📖 {scriptureTitle}</Title>
      </Header>
      {allVerses.map((part, partIndex) => (
        <ScriptureSection key={partIndex}>
          {scriptureParts.length > 1 && (
            <ScriptureReference $isFirst={partIndex === 0}>
              {part.reference}
            </ScriptureReference>
          )}
          <VerseList>
            {part.verses.map((verse, index) => {
              const prevVerse = index > 0 ? part.verses[index - 1] : null;
              const chapterLabel = getChapterLabel(verse, prevVerse);

              return (
                <React.Fragment key={index}>
                  {chapterLabel && (
                    <ChapterDivider
                      $isFirst={
                        index === 0 &&
                        (scriptureParts.length === 1 ||
                          partIndex > 0 ||
                          scriptureParts.length <= 1)
                      }
                    >
                      {chapterLabel}
                    </ChapterDivider>
                  )}
                  <VerseLine>
                    <VerseNumber>{verse.verse}</VerseNumber>
                    {verse.text}
                  </VerseLine>
                </React.Fragment>
              );
            })}
          </VerseList>
        </ScriptureSection>
      ))}
    </Container>
  );
};

const Container = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 25px;
  border-left: 3px solid #004094;
`;

const Header = styled.div`
  margin-bottom: 10px;
`;

const ScriptureSection = styled.div`
  &:not(:last-child) {
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e0e0e0;
  }
`;

const ScriptureReference = styled.div<{ $isFirst?: boolean }>`
  font-size: 13px;
  color: #666;
  font-weight: 600;
  margin-bottom: 8px;
  margin-top: ${({ $isFirst }) => ($isFirst ? "0" : "0")};
`;

const Title = styled.h3`
  font-size: 14px;
  color: #004094;
  font-weight: 700;
  margin: 0;
`;

const VerseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ChapterDivider = styled.div<{ $isFirst?: boolean }>`
  font-size: 12px;
  color: #004094;
  font-weight: 700;
  padding: ${({ $isFirst }) => ($isFirst ? "0 0 4px" : "8px 0 4px")};
  margin-top: ${({ $isFirst }) => ($isFirst ? "0" : "8px")};
  border-top: ${({ $isFirst }) => ($isFirst ? "none" : "1px dashed #ccc")};
`;

const VerseLine = styled.p`
  font-size: 14px;
  line-height: 1.7;
  color: #444;
  margin: 0;
  word-break: keep-all;
`;

const VerseNumber = styled.sup`
  font-size: 13px;
  color: #004094;
  font-weight: 700;
  margin-right: 5px;
`;

export default ScriptureText;
